import { useRef, useState } from 'react';
import { formatBytes } from '../lib/image';
import { uploadReceipt } from '../lib/upload';
import { ErrorNote } from './ui';

/**
 * TWO inputs, deliberately.
 *
 * `capture` is a hint, not a contract: Android honours it and opens the
 * camera, iOS Safari shows the photo/library/files sheet regardless, and it is
 * ignored entirely when `multiple` is present. A single capture-forced input
 * therefore gives an iOS user whose receipt is already in Photos a worse
 * experience. Two buttons cost one extra <input> and fix that.
 */
export function ReceiptCapture({
  reportId,
  onUploaded,
  disabled,
}: {
  reportId: string;
  onUploaded: (info: { receiptId: string; previewUrl: string; filename: string; contentType: string }) => void;
  disabled?: boolean;
}) {
  const cameraRef = useRef<HTMLInputElement>(null);
  const libraryRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  async function handleFiles(files: FileList | null, source: 'camera' | 'library') {
    if (!files?.length) return;
    setError(null);
    setNote(null);

    for (const file of Array.from(files)) {
      try {
        setProgress(0);
        const result = await uploadReceipt(reportId, file, source, setProgress);

        if (result.needsServerTranscode) {
          // Never fail silently on a codec: say plainly what happened and
          // let the submission proceed.
          setNote(
            `Stored "${file.name}" as-is — this device could not convert it for preview. It is attached and will submit fine.`,
          );
        } else {
          const saved = result.originalSize > 0 ? Math.round(result.originalSize / result.finalSize) : 1;
          setNote(
            `Uploaded ${formatBytes(result.finalSize)} (from ${formatBytes(result.originalSize)}, ${saved}× smaller).`,
          );
        }

        onUploaded({
          receiptId: result.receiptId,
          previewUrl: result.previewUrl,
          filename: file.name,
          contentType: file.type,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'That upload failed. Try again.');
      } finally {
        setProgress(null);
      }
    }
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <button type="button" className="btn-secondary" disabled={disabled || progress !== null}
                onClick={() => cameraRef.current?.click()}>
          Take photo
        </button>
        <button type="button" className="btn-secondary" disabled={disabled || progress !== null}
                onClick={() => libraryRef.current?.click()}>
          Choose file
        </button>
      </div>

      <input
        ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={(e) => { void handleFiles(e.target.files, 'camera'); e.target.value = ''; }}
      />
      <input
        ref={libraryRef} type="file" accept="image/*,application/pdf" multiple className="hidden"
        onChange={(e) => { void handleFiles(e.target.files, 'library'); e.target.value = ''; }}
      />

      {progress !== null ? (
        <div className="space-y-1">
          <div className="h-2 overflow-hidden rounded-full bg-slate-200">
            <div className="h-full bg-brand transition-all" style={{ width: `${Math.round(progress * 100)}%` }} />
          </div>
          <p className="text-xs text-slate-500">
            {progress < 0.1 ? 'Preparing the image…' : `Uploading… ${Math.round(progress * 100)}%`}
          </p>
        </div>
      ) : null}

      {note ? <p className="text-xs text-slate-500">{note}</p> : null}
      <ErrorNote>{error}</ErrorNote>
    </div>
  );
}
